import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from '@chakra-ui/react';
import { useSnackbar } from '@components/snackbar';
import { AddIcon, LeftArrowIcon, MoreIcon } from '@sketchnote/icons';
import { ComingSoonIconOnly, MoreActionMenu } from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import cn from 'classnames';
import { debounce, groupBy } from 'lodash';
import { useRouter } from 'next/router';
import * as React from 'react';

import { getFileType } from '..';
import { fileIconList, isTaskView, viewsWithoutFileHeader } from '../assets/data';
import { useFileAccess } from '../hooks/useFileAccess';
import { useFileStore } from '../store/useFileStore';
import { AgileNavBar } from './AgileNavbar';
import { EmbedFavicon, EmbedFaviconWithTitle } from './EmbedFavicon';
import { SelectFileView } from './select-file-view';

const { useState, useCallback, useEffect, useRef } = React;

export const FileViewNavbar = ({ isSticky }: { isSticky: boolean }): JSX.Element => {
  const [isSelectViewModalOpen, setIsSelectViewModalOpen] = useState(false);
  const [viewMenu, setViewMenu] = useState('');
  const [primaryViewMenu, setPrimaryViewMenu] = useState(false);
  const [isMoreMenuOpened, setIsMoreMenuOpened] = useState(false);
  const [isPopOpen, setIsPopOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [clickedViewId, setClickedViewId] = useState('');
  const [clickedViewType, setClickedViewType] = useState('');

  const {
    isOpen: isFileSubMenuOpen,
    onToggle: onSubMenuToggle,
    onClose: onFileSubMenuClose,
  } = useDisclosure();

  const {
    isOpen: isDocSubMenuOpen,
    onToggle: onDocSubMenuToggle,
    onClose: onDocSubMenuClose,
  } = useDisclosure();

  const {
    isOpen: isListSubMenuOpen,
    onToggle: onListSubMenuToggle,
    onClose: onListSubMenuClose,
  } = useDisclosure();

  const {
    isOpen: isWhiteboardSubMenuOpen,
    onToggle: onWhiteboardSubMenuToggle,
    onClose: onWhiteboardSubMenuClose,
  } = useDisclosure();

  useEffect(() => {
    if (!isPopOpen) {
      setViewMenu('');
      isDocSubMenuOpen && onDocSubMenuToggle();
      isFileSubMenuOpen && onSubMenuToggle();
      isWhiteboardSubMenuOpen && onWhiteboardSubMenuToggle();
      isListSubMenuOpen && onListSubMenuToggle();
    }
  }, [isPopOpen]);

  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  const fileId = router?.query?.slug as string;
  const [openSnackbar] = useSnackbar();

  const currentFile = useFileStore(
    useCallback((s) => s.files[router.query.slug as string], [router.query.slug as string])
  );

  const { isReadOnly } = useFileAccess();

  const onDeleteView = useFileStore((s) => s.onDeleteView);
  const onUpdateEmbedUrl = useFileStore((s) => s.onUpdateEmbedUrl);

  const handleViewMenuClick = async (option: any, viewId: string, type: string, name?: string) => {
    if (option.value === 'edit-url') {
      onUpdateEmbedUrl(fileId, viewId, '');
    }
  };

  const internalFileGroups = groupBy(currentFile?.internalFiles, 'view');

  const onDeleteViewClick = async (viewId: string, type: string) => {
    const response: any =
      currentFile?.id && (await onDeleteView({ fileId: currentFile?.id, viewId }));
    if (response) {
      openSnackbar({
        text: `${type[0]?.toUpperCase() + type.slice(1)} view deleted`,
        snackType: 'standard',
        position: 'bottom-center',
      });
      router.push(
        `/${mode}/${orgId}/files/${router.query.slug}?view=${currentFile?.primaryFile?.id}&type=${
          currentFile?.primaryFile?.type === 'board'
            ? currentFile?.primaryFile?.view
            : currentFile?.primaryFile?.type
        }`
      );
    }
  };

  const setViewIdNType = (id: string, view: string) => {
    setClickedViewId(id);
    setClickedViewType(view);
  };

  return currentFile?.authorisation ? (
    <div
      className={cn({
        'fixed top-50':
          (isSticky && router.query.type !== 'kanban' && router.query.type !== 'list') ||
          viewsWithoutFileHeader.includes(router.query.type as string),
        'w-full border-b border-t border-gray-200 z-50 bg-white ': true,
      })}
    >
      {/* Views without file-header/coverImage to be added in the below condition*/}
      {/* Views that donot use sticky navbar to be added in the below condition*/}
      <div
        className={cn({
          'flex items-center p-8 pr-16 pl-10 relative w-full': true,
          'pl-24': isSticky || viewsWithoutFileHeader.includes(router.query.type as string),
          'mx-auto':
            (!isSticky && !viewsWithoutFileHeader.includes(router.query.type as string)) ||
            router.query.type === 'kanban' ||
            router.query.type === 'list',
          'max-w-864 px-10 mx-auto ': !isSticky && router.query.type === 'document',
          'mobile:px-16 desktop:px-24 bigScreen:px-64':
            router.query.type !== 'document' &&
            !viewsWithoutFileHeader.includes(router.query.type as string),
        })}
      >
        <div className="flex mr-16 items-center max-w-93p overflow-x-scroll no-scrollbar">
          <div className="flex no-scrollbar pr-4" id="view-container">
            {currentFile?.file_hidden || (
              <>
                {currentFile?.primaryFile?.type === 'agile' ? (
                  <AgileNavBar file={currentFile?.id as string} />
                ) : (
                  <button
                    onClick={() => {
                      router.push(
                        `/${mode}/${orgId}/files/${router.query.slug}?view=${
                          currentFile?.primaryFile?.id
                        }&type=${
                          currentFile?.primaryFile?.type === 'board'
                            ? currentFile?.primaryFile?.view
                            : currentFile?.primaryFile?.type
                        }&viewId=${currentFile?.primaryFile?.id}`
                      );
                    }}
                  >
                    <a>
                      <div
                        className={`font-medium relative mt-1 flex items-center text-sm ${
                          router.query.view === currentFile?.primaryFile?.id &&
                          router.query.type === currentFile?.primaryFile?.view &&
                          (!router.query.viewId ||
                            router.query.viewId === currentFile?.primaryFile?.id)
                            ? 'text-blue-500 '
                            : 'text-gray-500'
                        } `}
                        onMouseOver={() => {
                          setPrimaryViewMenu(true);
                        }}
                        onMouseLeave={() => {
                          !isMoreMenuOpened && setPrimaryViewMenu(false);
                        }}
                      >
                        <span className="block mt-2 mr-4">
                          {currentFile?.primaryFile?.type !== 'embed' ? (
                            fileIconList[
                              `${
                                currentFile?.primaryFile?.type === 'board'
                                  ? currentFile?.primaryFile.view
                                  : currentFile?.primaryFile?.type
                              }`
                            ]
                          ) : (
                            <EmbedFavicon url={currentFile.primaryFile.data?.url} />
                          )}
                        </span>

                        {currentFile?.primaryFile?.type === 'board' ? (
                          currentFile?.primaryFile.view && (
                            <span>
                              {currentFile?.primaryFile.view.charAt(0).toUpperCase() +
                                currentFile?.primaryFile.view.slice(1)}
                            </span>
                          )
                        ) : (
                          <span className="w-70 truncate text-left">
                            {currentFile?.primaryFile && currentFile?.primaryFile?.name
                              ? currentFile?.primaryFile?.name
                              : 'Untitled'}
                          </span>
                        )}
                        {primaryViewMenu &&
                          !isReadOnly &&
                          (currentFile.primaryFile?.type !== 'embed' ? (
                            <MoreActionMenu
                              usePortal
                              classNames={{
                                labelWrap: 'bg-white shadow-sm rounded-sm px-5 absolute -right-10',
                                menuWrap: 'z-10 overflow-hidden',
                              }}
                              headerData={
                                <div className="text-xs text-gray-600 mx-auto p-8">
                                  This view is required
                                </div>
                              }
                              menuItems={[]}
                              onClickMenuItem={() => null}
                              onCloseMenu={() => {
                                setPrimaryViewMenu(false);
                              }}
                              placement="bottom"
                            />
                          ) : (
                            <MoreActionMenu
                              usePortal
                              classNames={{
                                labelWrap:
                                  'bg-white shadow-sm rounded-sm px-5 absolute -right-10 -top-8',
                                menuWrap: 'z-10 overflow-hidden',
                              }}
                              headerData={
                                currentFile.primaryFile.data?.url ? (
                                  <div className="hidden" />
                                ) : (
                                  <div className="text-xs text-gray-600 mx-auto p-8">
                                    This view is required
                                  </div>
                                )
                              }
                              menuItems={
                                currentFile.primaryFile.data?.url
                                  ? [{ label: 'Edit URL', value: 'edit-url' }]
                                  : []
                              }
                              onClickMenuItem={(e) =>
                                handleViewMenuClick(
                                  e,
                                  currentFile.primaryFile?.id as string,
                                  'embed',
                                  currentFile.primaryFile?.name
                                )
                              }
                              isOpen={isMoreMenuOpened}
                              onOpenMenu={() => {
                                setIsMoreMenuOpened(true);
                              }}
                              onCloseMenu={() => {
                                setPrimaryViewMenu(false);
                                setIsMoreMenuOpened(false);
                              }}
                              placement="bottom"
                            />
                          ))}
                      </div>
                    </a>
                  </button>
                )}
              </>
            )}

            {Object.keys(internalFileGroups).map((filetype, index) => {
              const [firstItem, ...restItems] = internalFileGroups[filetype];
              if (restItems.length > 0) {
                const { id, type, view, name } = firstItem;
                return type === 'embed' ? (
                  <ShowViewSubmenu
                    isSubMenuOpen={isFileSubMenuOpen}
                    id={id}
                    type={type}
                    view={view}
                    onClick={onSubMenuToggle}
                    onClose={onFileSubMenuClose}
                    setIsPopOpen={setIsPopOpen}
                    setIsDeleteModalOpen={setIsDeleteModalOpen}
                    setViewIdNType={setViewIdNType}
                  />
                ) : type === 'document' ? (
                  <ShowViewSubmenu
                    isSubMenuOpen={isDocSubMenuOpen}
                    id={id}
                    type={type}
                    view={view}
                    onClick={onDocSubMenuToggle}
                    onClose={onDocSubMenuClose}
                    setIsPopOpen={setIsPopOpen}
                    setIsDeleteModalOpen={setIsDeleteModalOpen}
                    setViewIdNType={setViewIdNType}
                  />
                ) : type === 'board' && view === 'list' ? (
                  <ShowViewSubmenu
                    isSubMenuOpen={isListSubMenuOpen}
                    id={id}
                    type={type}
                    view={view}
                    onClick={onListSubMenuToggle}
                    onClose={onListSubMenuClose}
                    setIsPopOpen={setIsPopOpen}
                    setIsDeleteModalOpen={setIsDeleteModalOpen}
                    setViewIdNType={setViewIdNType}
                  />
                ) : type === 'whiteboard' ? (
                  <ShowViewSubmenu
                    isSubMenuOpen={isWhiteboardSubMenuOpen}
                    id={id}
                    type={type}
                    view={view}
                    onClick={onWhiteboardSubMenuToggle}
                    onClose={onWhiteboardSubMenuClose}
                    setIsPopOpen={setIsPopOpen}
                    setIsDeleteModalOpen={setIsDeleteModalOpen}
                    setViewIdNType={setViewIdNType}
                  />
                ) : (
                  <></>
                );
              } else {
                const { id, type, view, name, data, viewId } = firstItem;
                return (
                  <div
                    className="flex flex-row"
                    onMouseOver={() => {
                      !isPopOpen && setViewMenu(`${id}-${view}`);
                    }}
                    onMouseLeave={() => {
                      !isPopOpen && setViewMenu('');
                    }}
                  >
                    {currentFile?.file_hidden && index === 0 ? (
                      <></>
                    ) : (
                      <span className="mx-12 block text-gray-500">|</span>
                    )}
                    <button
                      className="flex relative"
                      onClick={() => {
                        router.push(
                          `/${mode}/${orgId}/files/${router.query.slug}?view=${id}&type=${
                            type === 'board' ? view : type
                          }&viewId=${viewId}`
                        );
                      }}
                    >
                      <div
                        key={id}
                        className={`font-medium mt-1 flex items-center text-sm relative ${
                          router.query.viewId === viewId && router.query.type === view
                            ? 'text-blue-500'
                            : 'text-gray-500'
                        } `}
                      >
                        {type !== 'embed' ? (
                          <>
                            <span className="block mt-2 mr-4">
                              {fileIconList[`${type === 'board' ? view : type}`]}
                            </span>
                            {name && name.length > 0 ? (
                              <span className="mr-6 truncate max-w-80 w-70 text-left">{name}</span>
                            ) : (
                              <span className="capitalize mr-6">
                                {type === 'board' ? view : type}
                              </span>
                            )}
                          </>
                        ) : (
                          <EmbedFaviconWithTitle url={data?.url} name={name} />
                        )}
                      </div>
                    </button>
                    {viewMenu === `${id}-${view}` && !isReadOnly && (
                      <ShowPopover
                        id={id}
                        viewId={viewId}
                        type={type === 'board' ? view : type}
                        name={name}
                        setIsPopOpen={setIsPopOpen}
                        setIsDeleteModalOpen={setIsDeleteModalOpen}
                        setViewIdNType={setViewIdNType}
                      />
                    )}
                  </div>
                );
              }
            })}
          </div>
        </div>
        <Tippy
          content={'You do not have the required permission to add views to a file'}
          disabled={!isReadOnly}
        >
          <button
            className="text-sm text-gray-500 font-medium flex items-center"
            onClick={() => !isReadOnly && setIsSelectViewModalOpen(true)}
          >
            <AddIcon wrapperClass="pr-8" />
            View
          </button>
        </Tippy>

        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
          }}
        >
          <ModalOverlay />
          <ModalContent
            style={{
              maxWidth: '46rem',
              padding: '32px',
              marginTop: '20rem',
              borderRadius: '8px',
            }}
          >
            <ModalCloseButton />
            <p className="mb-32 text-sm text-gray-999">Are you sure you want to delete view? </p>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                }}
                className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
              >
                Cancel
              </button>
              <button
                className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
                onClick={(e) => {
                  onDeleteViewClick(clickedViewId, clickedViewType);
                  setIsDeleteModalOpen(false);
                }}
              >
                Delete
              </button>
            </div>
          </ModalContent>
        </Modal>

        <Modal isOpen={isSelectViewModalOpen} onClose={() => setIsSelectViewModalOpen(false)}>
          <ModalOverlay />
          <ModalCloseButton />
          <ModalContent
            width="auto"
            maxWidth="auto"
            margin="auto"
            borderRadius="0.8rem"
            className="static rounded-lg"
          >
            <ModalBody className="flex items-center justify-center text-left" padding="0px">
              <SelectFileView onClose={() => setIsSelectViewModalOpen(false)} />
            </ModalBody>
          </ModalContent>
        </Modal>
      </div>
    </div>
  ) : (
    <></>
  );
};

const ShowViewSubmenu = ({
  isSubMenuOpen,
  id,
  type,
  view,
  onClose,
  onClick,
  setIsPopOpen,
  setIsDeleteModalOpen,
  setViewIdNType,
}: {
  isSubMenuOpen: boolean;
  id: string;
  type: string;
  view: string;
  onClose: () => void;
  onClick: () => void;
  setIsPopOpen: (boolean) => void;
  setIsDeleteModalOpen: (boolean) => void;
  setViewIdNType: (id: string, type: string) => void;
}) => {
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;

  const currentFile = useFileStore(
    useCallback((s) => s.files[router.query.slug as string], [router.query.slug as string])
  );

  const { isReadOnly } = useFileAccess();

  const internalFileGroups = groupBy(currentFile?.internalFiles, 'view');
  const viewType = getFileType({ type, view });
  const embedBtn = useRef<HTMLButtonElement | null>(null);

  const isSelected = internalFileGroups[viewType].find(
    (view) => view.viewId === router.query.viewId
  );

  return (
    <Popover isOpen={isSubMenuOpen} onClose={onClose}>
      <span className="mx-12 block text-gray-500">|</span>
      <PopoverTrigger>
        <button className="flex relative" onClick={onClick}>
          <div
            key={`primary-${id}`}
            className={`font-medium mt-1 flex items-center text-sm relative ${
              isSelected ? 'text-blue-500 ' : 'text-gray-500'
            } `}
          >
            <span className="block mt-2 mr-4">{fileIconList[`${viewType}`]}</span>
            <span className="capitalize mr-6">
              {isSelected ? isSelected.name || viewType : viewType}
            </span>
            <span className="bg-primary-100 text-primary-600 rounded-full w-20 h-20 text-xs font-thin flex justify-center align-middle items-center">
              {internalFileGroups[viewType] && internalFileGroups[viewType].length}
            </span>
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent>
        <ul className="bg-white p-2 rounded-sm shadow-sm flex flex-col list-none">
          {internalFileGroups[viewType] &&
            internalFileGroups[viewType].map(({ id, view, viewId, name, type, data }) => (
              <li key={id} className="flex bg-white hover:bg-gray-100 items-center pr-8">
                <button
                  className="flex relative px-12 py-6 items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                    router.push(
                      `/${mode}/${orgId}/files/${router.query.slug}?view=${id}&type=${
                        type === 'board' ? view : type
                      }&viewId=${viewId}`
                    );
                  }}
                  ref={embedBtn}
                >
                  <div
                    key={id}
                    className={`font-medium mt-1 flex items-center text-sm relative ${
                      router.query.viewId === viewId && router.query.type === view
                        ? 'text-blue-500 '
                        : 'text-gray-500'
                    } `}
                  >
                    {type !== 'embed' ? (
                      <>
                        <span className="block mt-2 mr-6">{fileIconList[`${viewType}`]}</span>
                        <span className="capitalize mr-6 truncate max-w-80 w-80 text-left">
                          {name && name.length > 0 ? name : viewType}
                        </span>
                      </>
                    ) : (
                      <EmbedFaviconWithTitle url={data?.url} name={name} />
                    )}
                  </div>
                </button>
                {!isReadOnly && (
                  <ShowPopover
                    id={id}
                    viewId={viewId}
                    type={viewType}
                    name={name}
                    setIsPopOpen={setIsPopOpen}
                    setIsDeleteModalOpen={setIsDeleteModalOpen}
                    setViewIdNType={setViewIdNType}
                  />
                )}
              </li>
            ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

const ShowPopover = ({
  id,
  viewId,
  type,
  name,
  setIsPopOpen,
  setIsDeleteModalOpen,
  setViewIdNType,
}: {
  id: string;
  viewId: string;
  type: string;
  name: string;
  view?: string;
  setIsPopOpen: (boolean) => void;
  setIsDeleteModalOpen: (boolean) => void;
  setViewIdNType: (id: string, type: string) => void;
}) => {
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  const fileId = router?.query?.slug as string;

  const renameBtn = useRef<HTMLButtonElement | null>(null);
  const renameInput = useRef<HTMLInputElement | null>(null);

  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [typeName, setTypeName] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const onUpdateEmbedUrl = useFileStore((s) => s.onUpdateEmbedUrl);
  const onRenameView = useFileStore((s) => s.onRenameView);

  const debouncedRename = useCallback(
    debounce((fileId, viewId, name) => {
      onRenameView({ fileId, viewId, name });
    }, 500),
    []
  );

  return (
    <Popover
      onClose={() => {
        setIsPopOpen(false);
      }}
      onOpen={() => {
        setIsPopOpen(true);
      }}
    >
      <PopoverTrigger>
        <button
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <MoreIcon className="text-gray-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="bg-white rounded-md shadow-md">
        <Popover isOpen={isRenameOpen} onClose={() => setIsRenameOpen(false)}>
          <PopoverTrigger>
            <button
              className="w-115 mx-6 mt-6 text-xs font-medium py-6 pl-6 hover:bg-gray-100 rounded-sm text-left"
              onClick={(e) => {
                e.stopPropagation();
                setIsRenameOpen(true);
                setIsFocused(true);
                setTypeName(name);
                setTimeout(() => {
                  renameInput?.current?.focus();
                }, 100);
              }}
              ref={renameBtn}
            >
              Rename
            </button>
          </PopoverTrigger>
          <PopoverContent
            placement="right"
            className="bg-white p-12 m-20 rounded-md shadow-md"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <p className="py-4 text-left text-gray-500 text-sm items-center ">
              <LeftArrowIcon
                size={12}
                className="mr-8"
                onClick={() => {
                  setIsRenameOpen(false);
                }}
              />
              Rename
            </p>
            <input
              ref={renameInput}
              className={cn('bg-white py-8 border-b h-30 text-gray-500 capitalize text-sm', {
                'text-gray-999': isFocused,
              })}
              id="rename-input"
              placeholder={name && name.length > 0 ? name : type}
              value={typeName}
              onChange={(e) => {
                setTypeName(e.target.value);
                debouncedRename(fileId, viewId, e.target.value);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  setIsPopOpen(false);
                  setIsFocused(false);
                  setIsRenameOpen(false);
                }
              }}
              onFocus={(e) => {
                setIsFocused(true);
              }}
              onBlur={() => {
                setIsFocused(false);
              }}
            />
          </PopoverContent>
        </Popover>
        <button
          className="w-115 mx-6 mb-6 text-xs font-medium py-6 pl-6 hover:bg-gray-100 rounded-sm text-left"
          onClick={(e) => {
            setIsPopOpen(false);
            e.stopPropagation();
            setIsDeleteModalOpen(true);
            setViewIdNType(viewId, type);
          }}
        >
          Delete
        </button>
        {type === 'embed' && (
          <button
            className="w-115 mx-6 mb-6 text-xs font-medium py-6 pl-6 hover:bg-gray-100 rounded-sm text-left"
            onClick={(e) => {
              e.stopPropagation();
              setIsPopOpen(false);
              router.push(`/${mode}/${orgId}/files/${router.query.slug}?view=${id}&type=${type}`);
              onUpdateEmbedUrl(fileId, id, '');
            }}
          >
            Edit URL
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
};
